/**
 * Filter Data Panel for Demo Admin
 *
 * Client-side filtering of fetched demo data
 * Includes view mode toggle and location/shift filters
 */

import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip
} from '@mui/material';

/**
 * Filter Data Panel Component
 * Provides client-side filtering controls for fetched data
 *
 * @param {Object} props - Component props
 * @param {Array} props.locations - Array of location objects with id and name
 * @param {Array} props.selectedLocationIds - Currently selected location IDs for filtering
 * @param {Function} props.onLocationChange - Callback when location filter changes
 * @param {string} props.selectedShift - Currently selected shift filter
 * @param {Function} props.onShiftChange - Callback when shift filter changes
 * @param {boolean} props.disabled - Whether controls should be disabled
 * @param {Array} props.availableShifts - Array of shifts available in the fetched data
 */
const FilterDataPanel = ({
  locations = [],
  selectedLocationIds = [],
  onLocationChange,
  selectedShift = '',
  onShiftChange,
  disabled = false,
  availableShifts = []
}) => {
  // Handle location selection change
  const handleLocationChange = (event) => {
    const value = event.target.value;
    // Handle "all" selection
    if (value.includes('all')) {
      if (selectedLocationIds.length === locations.length) {
        onLocationChange([]);
      } else {
        onLocationChange(locations.map(loc => loc.id));
      }
    } else {
      onLocationChange(value);
    }
  };

  // Handle shift selection change
  const handleShiftChange = (event) => {
    onShiftChange(event.target.value);
  };

  // Get location name by ID
  const getLocationName = (id) => {
    const location = locations.find(loc => loc.id === id);
    return location ? location.name : id;
  };

  // Purple theme styling
  const selectSx = {
    height: '40px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#7C3AED',
      borderWidth: '2px'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#6D28D9'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#7C3AED'
    }
  };

  const labelSx = {
    color: '#7C3AED',
    '&.Mui-focused': {
      color: '#7C3AED'
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ color: '#7C3AED', mb: 2 }}>
        Filter Data
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
        {/* Location Filter */}
        <FormControl sx={{ minWidth: 250 }} size="small">
          <InputLabel id="filter-location-label" sx={labelSx}>
            Filter by Location
          </InputLabel>
          <Select
            labelId="filter-location-label"
            id="filter-location-select"
            multiple
            value={selectedLocationIds}
            onChange={handleLocationChange}
            input={<OutlinedInput label="Filter by Location" />}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <em>All Locations</em>;
              }
              if (selected.length === locations.length) {
                return 'All Locations';
              }
              if (selected.length <= 2) {
                return selected.map(id => getLocationName(id)).join(', ');
              }
              return `${selected.length} locations selected`;
            }}
            disabled={disabled || locations.length === 0}
            sx={selectSx}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300
                }
              }
            }}
          >
            {/* Select All option */}
            <MenuItem value="all">
              <Checkbox
                checked={selectedLocationIds.length === locations.length && locations.length > 0}
                indeterminate={selectedLocationIds.length > 0 && selectedLocationIds.length < locations.length}
              />
              <ListItemText primary="Select All" />
            </MenuItem>

            {locations.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                <Checkbox checked={selectedLocationIds.includes(location.id)} />
                <ListItemText primary={location.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Shift Filter */}
        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel id="filter-shift-label" sx={labelSx}>
            Filter by Shift
          </InputLabel>
          <Select
            labelId="filter-shift-label"
            id="filter-shift-select"
            value={selectedShift}
            onChange={handleShiftChange}
            label="Filter by Shift"
            disabled={disabled}
            sx={selectSx}
          >
            <MenuItem value="">
              <em>All Shifts</em>
            </MenuItem>
            {availableShifts.map((shift) => (
              <MenuItem key={shift} value={shift}>
                {shift}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Active filters chips */}
      {(selectedLocationIds.length > 0 && selectedLocationIds.length < locations.length) || selectedShift ? (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {selectedLocationIds.length > 0 && selectedLocationIds.length < locations.length && (
            <Chip
              label={`${selectedLocationIds.length} location${selectedLocationIds.length > 1 ? 's' : ''} filtered`}
              size="small"
              onDelete={() => onLocationChange([])}
              sx={{
                backgroundColor: 'rgba(124, 58, 237, 0.1)',
                color: '#7C3AED',
                '& .MuiChip-deleteIcon': {
                  color: '#7C3AED'
                }
              }}
            />
          )}
          {selectedShift && (
            <Chip
              label={`Shift: ${selectedShift}`}
              size="small"
              onDelete={() => onShiftChange('')}
              sx={{
                backgroundColor: 'rgba(124, 58, 237, 0.1)',
                color: '#7C3AED',
                '& .MuiChip-deleteIcon': {
                  color: '#7C3AED'
                }
              }}
            />
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default FilterDataPanel;
